import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useSpring, useTransform, useInView, useMotionValue, animate } from "framer-motion";

// ----- Scroll progress bar (site-wide) -----
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 22, mass: 0.35 });
  return (
    <motion.div
      style={{ scaleX, transformOrigin: "0% 50%" }}
      className="pointer-events-none fixed left-0 right-0 top-0 z-[60] h-[3px] bg-gradient-to-r from-[#0B2A4E] via-zoc-teal to-zoc-green"
      data-testid="scroll-progress"
      aria-hidden="true"
    />
  );
};

// ----- RevealText: masked word-by-word entrance -----
export const RevealText = ({
  as: Tag = "span",
  text,
  className = "",
  wordClassName = "",
  delay = 0,
  stagger = 0.05,
  gradient = false,
  ...rest
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const words = String(text).split(" ");
  // If gradient is requested, apply the gradient class per-word so background-clip:text
  // paints on the same box that holds the text (avoids transparent-text-over-transparent-bg bug).
  const perWord = `${gradient ? "zoc-gradient-text " : ""}${wordClassName}`.trim();

  return (
    <Tag ref={ref} className={className} aria-label={text} {...rest}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className={`inline-block will-change-transform ${perWord}`}
            initial={{ y: "110%" }}
            animate={inView ? { y: 0 } : { y: "110%" }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: delay + i * stagger }}
            aria-hidden="true"
          >
            {word}
          </motion.span>
          {i < words.length - 1 && <span aria-hidden="true">&nbsp;</span>}
        </span>
      ))}
    </Tag>
  );
};

// ----- Parallax wrapper: translate Y by scroll progress -----
export const Parallax = ({ children, offset = 60, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
  }, []);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={reduced ? {} : { y }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  );
};

// ----- CountUp: tween number from 0 → value when in view -----
export const CountUp = ({ value, duration = 1.4, prefix = "", suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const [display, setDisplay] = useState(0);
  const mv = useMotionValue(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, duration, mv]);
  const isFloat = value % 1 !== 0;
  const formatted = isFloat ? display.toFixed(1) : Math.round(display).toLocaleString();
  return (
    <span ref={ref} className={className} data-testid={`countup-${value}`}>
      {prefix}{formatted}{suffix}
    </span>
  );
};

// ----- Scale-in on scroll (for hero images) -----
export const ScrollScale = ({ children, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.96, 1, 1.06]);
  return (
    <div ref={ref} className={className}>
      <motion.div style={{ scale }} className="w-full h-full origin-center">{children}</motion.div>
    </div>
  );
};

// ----- SectionCrossfade: fades page bg color based on scroll depth -----
export const SectionCrossfade = () => {
  const { scrollYProgress } = useScroll();
  const bg = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    ["#FFFFFF", "#F8FAFC", "#F1F5F9", "#FFFBF5"]
  );
  return (
    <motion.div
      aria-hidden="true"
      style={{ backgroundColor: bg }}
      className="pointer-events-none fixed inset-0 -z-10"
      data-testid="section-crossfade"
    />
  );
};

// ----- StickyTOC: sub-nav that highlights section based on IntersectionObserver -----
export const StickyTOC = ({ sections = [] }) => {
  const [active, setActive] = useState(sections[0]?.id || "");
  useEffect(() => {
    if (!sections.length) return;
    const observers = sections.map((s) => {
      const el = document.getElementById(s.id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(s.id);
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, [sections]);

  if (!sections.length) return null;

  return (
    <>
      {/* Mobile: horizontal scroll pills sticky under navbar */}
      <nav
        data-testid="sticky-toc-mobile"
        aria-label="Section navigation"
        className="lg:hidden -mx-4 px-4 sticky top-16 z-30 bg-white/85 backdrop-blur border-b border-slate-200"
      >
        <ul className="flex gap-2 overflow-x-auto py-3 no-scrollbar">
          {sections.map((s) => (
            <li key={s.id} className="shrink-0">
              <a
                href={`#${s.id}`}
                data-testid={`toc-mobile-${s.id}`}
                className={`inline-flex whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                  active === s.id
                    ? "border-zoc-teal bg-zoc-teal/10 text-zoc-teal"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Desktop: sticky rail */}
      <nav
        data-testid="sticky-toc"
        aria-label="Section navigation"
        className="sticky top-24 hidden lg:block"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">On this page</p>
        <ul className="mt-4 space-y-2">
          {sections.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                data-testid={`toc-${s.id}`}
                className={`group flex items-center gap-2 py-1.5 text-sm transition-colors duration-200 ${
                  active === s.id ? "text-[#0B2A4E] font-semibold" : "text-slate-500 hover:text-[#0B2A4E]"
                }`}
              >
                <span
                  className={`inline-block h-0.5 rounded-full transition-all duration-300 ${
                    active === s.id ? "w-8 bg-zoc-teal" : "w-3 bg-slate-300 group-hover:w-5 group-hover:bg-slate-400"
                  }`}
                />
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export const MagneticHover = ({ children, strength = 0.2, className = "" }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 20 });
  const sy = useSpring(y, { stiffness: 300, damping: 20 });
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};
